import { useEffect, useSyncExternalStore } from "react";

export type Theme = "dark" | "light" | "high-contrast";

const STORAGE_KEY = "chula:theme";
// AXIOM: warm paper is the flagship surface — light leads the cycle.
const CYCLE: Theme[] = ["light", "dark", "high-contrast"];
const DEFAULT_THEME: Theme = "light";

function readSavedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "light" || v === "high-contrast" ? (v as Theme) : null;
}

// ── Module-level external store ─────────────────────────────────────────────
// One shared theme value + listener set, consumed via useSyncExternalStore,
// so every useTheme() instance (TopBar's toggle, App's basemap selection, …)
// observes the same state. Previously each instance held its own useState
// copy: toggling the theme in TopBar never re-rendered App, so the basemap
// stayed on the old theme until reload.

let currentTheme: Theme | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): Theme {
  if (currentTheme == null) currentTheme = readSavedTheme() ?? DEFAULT_THEME;
  return currentTheme;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    // When the last consumer unmounts, drop the cached value so the next
    // mount re-reads localStorage (fresh mounts and tests stay honest).
    if (listeners.size === 0) currentTheme = null;
  };
}

function setTheme(t: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // Safari private mode may throw
  }
  if (t === currentTheme) return;
  currentTheme = t;
  for (const listener of [...listeners]) listener();
}

function toggle(): void {
  const next = CYCLE[(CYCLE.indexOf(getSnapshot()) + 1) % CYCLE.length];
  setTheme(next);
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  // Light is the default unless the operator has explicitly chosen otherwise.
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.style.colorScheme = theme === "high-contrast" ? "dark" : theme;
  }, [theme]);

  return { theme, setTheme, toggle };
}
